<!-- Click-out backdrop -->
<div class="vf-ca-backdrop" *ngIf="isOpen" (click)="close()"></div>

<!-- Right drawer. Always mounted: inert while closed (Z-4 FX-6, OB-C-18) and hidden once the slide-out played (.scss).
     FU-Z4 x2: open, it is a modal dialog named by its title; show() moves the focus to it, Escape closes it. -->
<div
    #drawer
    class="vf-ca-drawer d-flex flex-column"
    role="dialog"
    aria-modal="true"
    aria-labelledby="vf-ca-title"
    tabindex="-1"
    [class.is-open]="isOpen"
    [attr.inert]="inertAttr"
    (keydown.escape)="onDrawerEscape($event)"
    (keydown.tab)="onDrawerTab($event, false)"
    (keydown.shift.tab)="onDrawerTab($event, true)"
>
    <!-- Header -->
    <div class="vf-ca-header d-flex align-items-center justify-content-between flex-shrink-0 w-100">
        <h2 id="vf-ca-title" class="mb-0 text-gray-900" style="font-size: 15px; font-weight: 600">Connected apps</h2>
        <button type="button" class="vf-ca-icon-btn" (click)="close()" aria-label="Close">
            <lucide-icon name="x" [size]="16" class="text-gray-500"></lucide-icon>
        </button>
    </div>

    <!-- Body -->
    <div class="flex-fill overflow-auto w-100">
        <div class="vf-ca-body-wrap">
            <!-- Microsoft 365 connection -->
            <div class="vf-ca-app-section w-100">
                <div class="vf-ca-app-row-inner d-flex align-items-center gap-12">
                    <div class="vf-ca-app-icon flex-shrink-0 d-flex align-items-center justify-content-center">
                        <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="11" height="11" fill="#F25022"></rect>
                            <rect y="12" width="11" height="11" fill="#00A4EF"></rect>
                            <rect x="12" width="11" height="11" fill="#7FBA00"></rect>
                            <rect x="12" y="12" width="11" height="11" fill="#FFB900"></rect>
                        </svg>
                    </div>
                    <div class="flex-fill min-w-0">
                        <div class="d-flex align-items-center gap-8">
                            <span class="text-gray-900" style="font-size: 14px; font-weight: 500">Microsoft 365</span>
                            <div class="d-flex align-items-center gap-8">
                                <span class="vf-ca-dot flex-shrink-0" [class.connected]="m365.isConnected"></span>
                                <span class="text-xs text-gray-500">{{ m365.label }}</span>
                            </div>
                        </div>
                        <div class="text-xs text-gray-400 text-truncate">{{ connectedEmail }}</div>
                    </div>
                    <button
                        *ngIf="m365.isConnected"
                        type="button"
                        class="vf-ca-gear flex-shrink-0"
                        [class.active]="actionsOpen"
                        (click)="actionsOpen = !actionsOpen"
                        aria-label="Microsoft 365 connection settings"
                    >
                        <lucide-icon name="settings" [size]="14"></lucide-icon>
                    </button>
                    <button
                        *ngIf="m365.showConnect"
                        type="button"
                        class="vf-ca-connect-btn flex-shrink-0"
                        (click)="connectMicrosoft()"
                    >
                        Connect
                    </button>
                </div>

                <!-- Reconnect / Disconnect (revealed by the gear, only when connected) -->
                <div *ngIf="m365.isConnected && actionsOpen" class="vf-ca-actions d-flex align-items-center gap-8">
                    <button type="button" class="vf-ca-action-btn d-flex align-items-center" (click)="reconnect()">
                        <lucide-icon name="refresh-cw" [size]="12"></lucide-icon>
                        <span>Reconnect</span>
                    </button>
                    <button
                        type="button"
                        class="vf-ca-action-btn danger d-flex align-items-center"
                        (click)="disconnect()"
                    >
                        <lucide-icon name="unplug" [size]="12"></lucide-icon>
                        <span>Disconnect</span>
                    </button>
                </div>
            </div>

            <!-- EMAIL -->
            <div class="vf-ca-section-label">
                <span class="text-sm text-gray-500">Email</span>
            </div>
            <div class="vf-ca-group">
                <button
                    type="button"
                    class="vf-ca-expand-btn d-flex align-items-center w-100"
                    style="gap: 16px"
                    (click)="togglePanel('email')"
                    [attr.aria-expanded]="openPanel === 'email'"
                >
                    <div class="flex-fill min-w-0 text-left">
                        <div class="text-gray-900" style="font-size: 14px">Email processing</div>
                    </div>
                    <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ emailSummary }}</span>
                    <span class="vf-ca-row-gear flex-shrink-0">
                        <lucide-icon
                            [name]="openPanel === 'email' ? 'chevron-down' : 'settings'"
                            [size]="openPanel === 'email' ? 16 : 14"
                        ></lucide-icon>
                    </span>
                </button>

                <div *ngIf="openPanel === 'email'" class="vf-ca-expand-panel">
                    <div class="d-flex align-items-start justify-content-between" style="gap: 16px">
                        <p class="text-sm text-gray-500 flex-fill mb-0" style="line-height: 1.375">
                            Trovve turns client emails into tasks so you never miss a lead or a request. Your firm's
                            rules apply.
                        </p>
                        <button
                            type="button"
                            class="vf-switch flex-shrink-0"
                            [class.is-on]="settings.emailProcessingEnabled"
                            role="switch"
                            [attr.aria-checked]="settings.emailProcessingEnabled"
                            (click)="toggleEmailProcessing()"
                        >
                            <span class="vf-switch-knob"></span>
                        </button>
                    </div>
                    <!-- Always shown: flag-to-task is INDEPENDENT of the master processing toggle (the worker
                         runs the flag path first, regardless of the per-user Phase-2 gate). A user can turn off
                         automatic processing but keep flagging an email to make it a task. -->
                    <div
                        class="vf-ca-flagged-row d-flex align-items-start justify-content-between"
                        style="gap: 16px; margin-top: 12px; padding-top: 12px"
                    >
                        <div class="flex-fill min-w-0">
                            <div class="text-gray-900" style="font-size: 14px; font-weight: 500">
                                Process my flagged emails
                            </div>
                            <p class="text-sm text-gray-500 mb-0" style="margin-top: 4px; line-height: 1.375">
                                Any email you flag in Outlook becomes a task.
                            </p>
                        </div>
                        <button
                            type="button"
                            class="vf-switch flex-shrink-0"
                            [class.is-on]="settings.processFlaggedEmailsEnabled"
                            role="switch"
                            [attr.aria-checked]="settings.processFlaggedEmailsEnabled"
                            (click)="toggleFlagged()"
                        >
                            <span class="vf-switch-knob"></span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Part 7 §6: What Trovve remembers (per-user sender memory: view / forget / reset) -->
            <div class="vf-ca-group">
                <button
                    type="button"
                    class="vf-ca-expand-btn d-flex align-items-center w-100"
                    style="gap: 16px"
                    (click)="togglePanel('memory')"
                    [attr.aria-expanded]="openPanel === 'memory'"
                >
                    <div class="flex-fill min-w-0 text-left">
                        <div class="text-gray-900" style="font-size: 14px">What Trovve remembers</div>
                    </div>
                    <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ memorySummary }}</span>
                    <span class="vf-ca-row-gear flex-shrink-0">
                        <lucide-icon
                            [name]="openPanel === 'memory' ? 'chevron-down' : 'settings'"
                            [size]="openPanel === 'memory' ? 16 : 14"
                        ></lucide-icon>
                    </span>
                </button>

                <div *ngIf="openPanel === 'memory'" class="vf-ca-expand-panel">
                    <p class="text-sm text-gray-500 mb-0" style="line-height: 1.375">
                        As you confirm or dismiss reviews, Trovve learns how to sort emails from these senders. Remove
                        one to forget it, or reset everything to start fresh.
                    </p>

                    <!-- Loading -->
                    <div *ngIf="memoryLoading" class="text-sm text-gray-400" style="margin-top: 12px">Loading…</div>

                    <!-- Empty -->
                    <div
                        *ngIf="!memoryLoading && memoryLoaded && !memoryRows.length"
                        class="text-sm text-gray-400"
                        style="margin-top: 12px; line-height: 1.375"
                    >
                        Nothing remembered yet. Trovve starts learning the first time you confirm or dismiss a review.
                    </div>

                    <!-- Remembered-sender list -->
                    <div *ngIf="!memoryLoading && memoryRows.length" class="vf-ca-mem-list">
                        <div *ngFor="let row of memoryRows; trackBy: trackByMemoryRow" class="vf-ca-mem-row">
                            <div class="flex-fill min-w-0">
                                <div class="d-flex align-items-center gap-8">
                                    <span class="vf-ca-mem-email text-truncate">{{ row.email }}</span>
                                    <span class="vf-ca-mem-chip flex-shrink-0" [ngClass]="row.dispositionClass">{{
                                        row.dispositionLabel
                                    }}</span>
                                </div>
                                <div class="vf-ca-mem-meta text-truncate">
                                    {{ row.seenText
                                    }}<ng-container *ngIf="row.lastSeenText"> · {{ row.lastSeenText }}</ng-container>
                                </div>
                            </div>
                            <button
                                type="button"
                                class="vf-ca-mem-x flex-shrink-0"
                                (click)="removeMemory(row)"
                                [attr.aria-label]="'Forget ' + row.email"
                            >
                                <lucide-icon name="x" [size]="12"></lucide-icon>
                            </button>
                        </div>
                    </div>

                    <!-- Reset all -->
                    <button
                        *ngIf="!memoryLoading && memoryRows.length"
                        type="button"
                        class="vf-ca-mem-reset d-flex align-items-center"
                        (click)="resetMemory()"
                    >
                        <lucide-icon name="rotate-ccw" [size]="12"></lucide-icon>
                        <span>Reset what Trovve remembers</span>
                    </button>
                </div>
            </div>

            <!-- CALENDAR (E11) — only when the FE flag is on and the workspace has Calendar enabled -->
            <ng-container *ngIf="showCalendar">
                <div class="vf-ca-section-label">
                    <span class="text-sm text-gray-500">Calendar</span>
                </div>

                <!-- Calendar sync -->
                <div class="vf-ca-group">
                    <button
                        type="button"
                        class="vf-ca-expand-btn d-flex align-items-center w-100"
                        style="gap: 16px"
                        (click)="togglePanel('calsync')"
                        [attr.aria-expanded]="openPanel === 'calsync'"
                    >
                        <div class="flex-fill min-w-0 text-left">
                            <div class="text-gray-900" style="font-size: 14px">Calendar sync</div>
                        </div>
                        <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ calendarSyncSummary }}</span>
                        <span class="vf-ca-row-gear flex-shrink-0">
                            <lucide-icon
                                [name]="openPanel === 'calsync' ? 'chevron-down' : 'settings'"
                                [size]="openPanel === 'calsync' ? 16 : 14"
                            ></lucide-icon>
                        </span>
                    </button>
                    <div *ngIf="openPanel === 'calsync'" class="vf-ca-expand-panel">
                        <div class="d-flex align-items-start justify-content-between" style="gap: 16px">
                            <p class="text-sm text-gray-500 flex-fill mb-0" style="line-height: 1.375">
                                Sync your Outlook calendar so your events appear in Trovve and on the firm Calendar.
                            </p>
                            <button
                                type="button"
                                class="vf-switch flex-shrink-0"
                                [class.is-on]="calendar.calendarSyncEnabled"
                                role="switch"
                                [attr.aria-checked]="calendar.calendarSyncEnabled"
                                (click)="toggleCalendarSyncEnabled()"
                            >
                                <span class="vf-switch-knob"></span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Sync meetings to My Day -->
                <div class="vf-ca-group">
                    <button
                        type="button"
                        class="vf-ca-expand-btn d-flex align-items-center w-100"
                        style="gap: 16px"
                        (click)="togglePanel('sync')"
                        [attr.aria-expanded]="openPanel === 'sync'"
                    >
                        <div class="flex-fill min-w-0 text-left">
                            <div class="text-gray-900" style="font-size: 14px">Sync meetings to My Day</div>
                        </div>
                        <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ syncSummary }}</span>
                        <span class="vf-ca-row-gear flex-shrink-0">
                            <lucide-icon
                                [name]="openPanel === 'sync' ? 'chevron-down' : 'settings'"
                                [size]="openPanel === 'sync' ? 16 : 14"
                            ></lucide-icon>
                        </span>
                    </button>
                    <div *ngIf="openPanel === 'sync'" class="vf-ca-expand-panel">
                        <div class="d-flex align-items-start justify-content-between" style="gap: 16px">
                            <p class="text-sm text-gray-500 flex-fill mb-0" style="line-height: 1.375">
                                Today's meetings show up in My Day.
                            </p>
                            <button
                                type="button"
                                class="vf-switch flex-shrink-0"
                                [class.is-on]="calendar.syncToMyDay"
                                role="switch"
                                [attr.aria-checked]="calendar.syncToMyDay"
                                (click)="toggleSyncToMyDay()"
                            >
                                <span class="vf-switch-knob"></span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Calendar visibility (tighten-only) -->
                <div class="vf-ca-group">
                    <button
                        type="button"
                        class="vf-ca-expand-btn d-flex align-items-center w-100"
                        style="gap: 16px"
                        (click)="togglePanel('visibility')"
                        [attr.aria-expanded]="openPanel === 'visibility'"
                    >
                        <div class="flex-fill min-w-0 text-left">
                            <div class="text-gray-900" style="font-size: 14px">Calendar visibility</div>
                        </div>
                        <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ visibilitySummary }}</span>
                        <span class="vf-ca-row-gear flex-shrink-0">
                            <lucide-icon
                                [name]="openPanel === 'visibility' ? 'chevron-down' : 'settings'"
                                [size]="openPanel === 'visibility' ? 16 : 14"
                            ></lucide-icon>
                        </span>
                    </button>
                    <div *ngIf="openPanel === 'visibility'" class="vf-ca-expand-panel">
                        <div class="text-gray-700" style="font-size: 14px; margin-bottom: 8px">
                            How your calendar appears to the team
                        </div>
                        <div class="vf-cal-seg">
                            <button
                                type="button"
                                class="vf-cal-seg-btn"
                                [class.is-on]="calendar.ownVisibility === visFull"
                                [disabled]="!canChooseFull"
                                (click)="setMyVisibility(visFull)"
                            >
                                Full details
                            </button>
                            <button
                                type="button"
                                class="vf-cal-seg-btn"
                                [class.is-on]="calendar.ownVisibility === visBusy"
                                [disabled]="!canChooseFull"
                                (click)="setMyVisibility(visBusy)"
                            >
                                Busy only
                            </button>
                        </div>
                        <p
                            *ngIf="canChooseFull"
                            class="text-gray-400 mb-0"
                            style="font-size: 14px; margin-top: 10px; line-height: 1.375"
                        >
                            Your admin sets the firm-wide default. You can make yours more private — not less.
                        </p>
                        <p
                            *ngIf="!canChooseFull"
                            class="text-gray-400 mb-0"
                            style="font-size: 14px; margin-top: 10px; line-height: 1.375"
                        >
                            Your firm shows calendars as Busy by default. Your admin manages this.
                        </p>
                    </div>
                </div>

                <!-- Who can access your calendar (inline grants) -->
                <div class="vf-ca-group">
                    <button
                        type="button"
                        class="vf-ca-expand-btn d-flex align-items-center w-100"
                        style="gap: 16px"
                        (click)="togglePanel('access')"
                        [attr.aria-expanded]="openPanel === 'access'"
                    >
                        <div class="flex-fill min-w-0 text-left">
                            <div class="text-gray-900" style="font-size: 14px">Who can access your calendar</div>
                        </div>
                        <span class="text-sm text-gray-500 text-truncate flex-shrink-0">{{ accessSummary }}</span>
                        <span class="vf-ca-row-gear flex-shrink-0">
                            <lucide-icon
                                [name]="openPanel === 'access' ? 'chevron-down' : 'settings'"
                                [size]="openPanel === 'access' ? 16 : 14"
                            ></lucide-icon>
                        </span>
                    </button>
                    <div *ngIf="openPanel === 'access'" class="vf-ca-expand-panel">
                        <ng-container *ngIf="showAccessList; else accessAnyone">
                            <div class="vf-ca-grant-list">
                                <div *ngFor="let row of grantRows; trackBy: trackByGrantRow" class="vf-ca-grant-row">
                                    <span class="vf-ca-grant-avatar flex-shrink-0">{{ row.initial }}</span>
                                    <span class="flex-fill min-w-0 vf-ca-grant-name text-truncate">{{ row.name }}</span>
                                    <span class="vf-ca-grant-badge flex-shrink-0">{{ row.label }}</span>
                                    <div class="d-flex align-items-center flex-shrink-0" style="gap: 6px">
                                        <span class="vf-ca-grant-added">{{ row.addedByText }}</span>
                                        <button
                                            *ngIf="row.canRemove"
                                            type="button"
                                            class="vf-ca-grant-x"
                                            (click)="removePerson(row.grant)"
                                            [attr.aria-label]="'Remove ' + row.name"
                                        >
                                            <lucide-icon name="x" [size]="11"></lucide-icon>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="position-relative" style="margin-top: 12px">
                                <button
                                    type="button"
                                    class="vf-ca-addperson"
                                    (click)="addPersonOpen = !addPersonOpen"
                                    [disabled]="!availableAccessUsers.length"
                                >
                                    <lucide-icon name="plus" [size]="14"></lucide-icon><span>Add person</span>
                                </button>
                                <ng-container *ngIf="addPersonOpen">
                                    <div class="vf-ca-dd-backdrop" (click)="addPersonOpen = false"></div>
                                    <div class="vf-ca-dd-panel">
                                        <a
                                            *ngFor="let u of availableAccessUsers; trackBy: trackByUserId"
                                            href="javascript:;"
                                            class="vf-ca-dd-item"
                                            (click)="addPerson(u)"
                                            >{{ u.name }}</a
                                        >
                                        <div *ngIf="!availableAccessUsers.length" class="vf-ca-dd-empty">
                                            Everyone already has access
                                        </div>
                                    </div>
                                </ng-container>
                            </div>

                            <p class="text-gray-400 mb-0" style="font-size: 14px; margin-top: 12px; line-height: 1.375">
                                People you add can put events on your calendar and see it in full. They're notified.
                            </p>
                            <p class="text-gray-400 mb-0" style="font-size: 14px; margin-top: 6px; line-height: 1.375">
                                Your firm-wide default is managed by your admin in
                                <span class="vf-ca-link">Company settings</span>.
                            </p>
                        </ng-container>
                        <ng-template #accessAnyone>
                            <p class="text-gray-400 mb-0" style="font-size: 14px; line-height: 1.375">
                                Anyone on the team can book and see calendars. Your admin manages this.
                            </p>
                        </ng-template>
                    </div>
                </div>
            </ng-container>
        </div>
    </div>

    <vf-spinner *ngIf="isLoading"></vf-spinner>
</div>
